import { useCallback, useEffect, useState } from "react";
import { Copy, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Field } from "@/components/common";
import { PasswordInput } from "@/components/PasswordInput";
import { adminApi, brl, errMsg, fmtDate } from "@/lib/api";

const sel = "h-10 w-full rounded-md border bg-transparent px-3 text-sm";

function BoySettings() {
  const [f, setF] = useState(null);
  const [secret, setSecret] = useState("");
  useEffect(() => {
    adminApi.get("/boy/settings").then((r) => setF({ ...r.data, fee_pct: String(r.data.fee_pct) })).catch(() => {});
  }, []);
  if (!f) return null;
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    try {
      const { data } = await adminApi.put("/boy/settings", { fee_pct: Number(String(f.fee_pct).replace(",", ".")) || 0, mode: f.mode, period: f.period, client_id: f.client_id, client_secret: secret });
      setF({ ...data, fee_pct: String(data.fee_pct) });
      setSecret("");
      toast.success("Boy Express salvo");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const copy = () => navigator.clipboard.writeText(f.redirect_uri).then(() => toast.success("URL copiada")).catch(() => toast.error("Copie a URL manualmente"));
  return (
    <div className="card-surface grid max-w-3xl gap-5 p-6 sm:grid-cols-2" data-testid="admin-boy-settings">
      <div className="sm:col-span-2"><p className="font-semibold">Boy Express</p><p className="text-xs text-muted-foreground">O valor mensal e a liberação por plano ficam na aba Planos, junto com os outros módulos.</p></div>
      <Field label="Taxa do desenvolvedor por corrida (%)" hint="Sobre o valor de cada corrida paga"><Input type="number" min="0" max="100" step="0.1" value={f.fee_pct} onChange={set("fee_pct")} data-testid="admin-boy-fee-input" /></Field>
      <Field label="Como receber a taxa">
        <select value={f.mode} onChange={set("mode")} className={sel} data-testid="admin-boy-mode-select">
          <option value="direto">Direto em cada corrida (Mercado Pago)</option>
          <option value="periodo">Fatura por período</option>
        </select>
      </Field>
      <Field label="Período da fatura" hint={f.mode === "direto" ? "Usado para as empresas que ainda não conectaram o Mercado Pago" : "A empresa paga com Pix na sua conta de Formas de Recebimento"}>
        <select value={f.period} onChange={set("period")} className={sel} data-testid="admin-boy-period-select">
          <option value="semanal">Semanal</option><option value="quinzenal">Quinzenal</option><option value="mensal">Mensal</option>
        </select>
      </Field>
      {f.mode === "direto" && (
        <>
          <div className="space-y-2 rounded-lg border bg-muted/40 p-4 text-sm sm:col-span-2" data-testid="admin-boy-direct-help">
            <p>Crie uma aplicação no painel de desenvolvedores do Mercado Pago, cadastre esta URL de redirecionamento e cole o Client ID e o Client Secret. Cada empresa conecta a conta dela em Boy Express → Configurações.</p>
            <div className="flex items-center gap-2"><code className="min-w-0 flex-1 break-all rounded bg-background px-2 py-1 text-xs" data-testid="admin-boy-redirect">{f.redirect_uri}</code><Button size="sm" variant="outline" onClick={copy}><Copy className="h-4 w-4" /></Button></div>
          </div>
          <Field label="Client ID"><Input value={f.client_id} onChange={set("client_id")} data-testid="admin-boy-client-id-input" /></Field>
          <Field label="Client Secret" hint={f.has_secret ? `Salvo (${f.secret_hint}). Deixe em branco para manter.` : "Fica guardado criptografado"}>
            <PasswordInput value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="off" testId="admin-boy-secret-input" />
          </Field>
        </>
      )}
      <Button onClick={save} className="h-11 sm:col-span-2" data-testid="admin-boy-save-btn"><Save className="mr-2 h-4 w-4" />Salvar Boy Express</Button>
    </div>
  );
}

function BoyFees() {
  const [d, setD] = useState(null);
  const load = useCallback(() => adminApi.get("/boy/fees").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
  }, [load]);
  const paid = async (i) => {
    if (!window.confirm(`Marcar a fatura de ${i.name} (${brl(i.amount)}) como paga?`)) return;
    try {
      await adminApi.post(`/boy/invoices/${i.id}/paid`);
      toast.success("Fatura marcada como paga");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!d) return null;
  return (
    <div className="card-surface space-y-4 p-6" data-testid="admin-boy-fees">
      <p className="font-semibold">Taxas por empresa</p>
      {!d.rows.length ? <p className="text-sm text-muted-foreground">Nenhuma corrida paga ainda.</p> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Empresa</TableHead><TableHead className="text-right">Corridas</TableHead><TableHead className="text-right">Volume</TableHead><TableHead className="text-right">Taxa total</TableHead><TableHead className="text-right">Direto</TableHead><TableHead className="text-right">Em aberto</TableHead><TableHead className="text-right">Pago</TableHead></TableRow></TableHeader>
            <TableBody>
              {d.rows.map((r) => (
                <TableRow key={r.tenant_id} data-testid={`admin-boy-row-${r.tenant_id}`}>
                  <TableCell>{r.name}<span className="block font-mono text-xs text-muted-foreground">{r.access_number}</span></TableCell>
                  <TableCell className="text-right">{r.rides}</TableCell><TableCell className="text-right">{brl(r.volume)}</TableCell><TableCell className="text-right font-semibold">{brl(r.fee)}</TableCell>
                  <TableCell className="text-right">{brl(r.direct)}</TableCell><TableCell className="text-right text-amber-600">{brl(r.open)}</TableCell><TableCell className="text-right text-emerald-600">{brl(r.paid)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {d.open_invoices.length > 0 && (
        <div className="space-y-2">
          <p className="eyebrow">Faturas em aberto</p>
          {d.open_invoices.map((i) => (
            <div key={i.id} className="flex flex-wrap items-center gap-3 rounded-lg border p-3 text-sm" data-testid={`admin-boy-invoice-${i.id}`}>
              <span className="min-w-0 flex-1"><b>{i.name}</b> · {fmtDate(i.start)} a {fmtDate(new Date(new Date(i.end).getTime() - 86400000).toISOString())} · {i.rides} corrida(s)</span>
              <span className="font-mono font-semibold">{brl(i.amount)}</span>
              <Button size="sm" variant="outline" onClick={() => paid(i)} data-testid={`admin-boy-invoice-paid-${i.id}`}>Marcar como paga</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function BoyExpressTab() {
  return <div className="space-y-6"><BoySettings /><BoyFees /></div>;
}
